import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NavBar from '../components/NavBar/Navigation';
import Footer from '../components/Footer/Footer';
import OurParksBanner from '../assets/ourparks.png';
import './OurParks.css';


const MONDAY_API_KEY = process.env.REACT_APP_MONDAY_TOKEN || '';
const BOARD_ID = '1857844707';
const LC_COLUMN_ID = 'board_relation_mkntssvz';
const CARTO_KEY = process.env.REACT_APP_CARTO_KEY || '';

// remove once the map is confirmed working
if (!CARTO_KEY) {
  console.warn('[OurParks] REACT_APP_CARTO_KEY is empty — restart the dev server after editing .env');
}


function stripSiteCode(name) {
  const idx = name.indexOf(' - ');
  return idx !== -1 ? name.slice(idx + 3).trim() : name.trim();
}


function formatArea(raw) {
  if (!raw) return '';
  const clean = raw.trim();
  if (/m²|m2/i.test(clean)) return clean.replace(/m2/gi, 'm²');
  return `${clean} m²`;
}


/* "12,450 m²" -> { value: "12,450", unit: "m²" } */
function splitArea(area) {
  if (!area) return null;
  const m = area.match(/^(.*?)\s*(m²)\s*$/i);
  if (m) return { value: m[1].trim(), unit: 'm²' };
  return { value: area.trim(), unit: '' };
}


/* numeric value of an area string, for the totals panel */
function areaToNumber(area) {
  if (!area) return 0;
  const n = parseFloat(area.replace(/[^\d.]/g, ''));
  return isNaN(n) ? 0 : n;
}


/* diacritic-insensitive compare so "gnien" matches "Ġnien", "hal" matches "Ħal" */
function normalizeText(str) {
  return (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/ħ/g, 'h');
}


function buildLabelMap(settingsStr) {
  const map = {};
  try {
    const s = JSON.parse(settingsStr || '{}');
    if (s.labels && !Array.isArray(s.labels)) {
      Object.entries(s.labels).forEach(([k, v]) => {
        map[String(k)] = typeof v === 'string' ? v : (v?.name || v?.label || '');
      });
    }
    if (Array.isArray(s.labels)) {
      s.labels.forEach((v, i) => { if (v) map[String(i)] = typeof v === 'string' ? v : (v?.name || ''); });
    }
    if (s.labels_colors) {
      Object.entries(s.labels_colors).forEach(([k, v]) => {
        if (!map[String(k)]) map[String(k)] = v?.label || '';
      });
    }
  } catch (_) {}
  return map;
}


async function fetchParks() {
  const schemaRes = await fetch('https://api.monday.com/v2', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': MONDAY_API_KEY,
      'API-Version': '2024-01',
    },
    body: JSON.stringify({
      query: `query {
        boards(ids: ${BOARD_ID}) {
          columns { id title type settings_str }
        }
      }`,
    }),
  });

  const schemaData = await schemaRes.json();
  if (schemaData.errors) throw new Error(schemaData.errors[0]?.message || 'Schema error');

  const columns = schemaData?.data?.boards?.[0]?.columns || [];
  const findCol = (kw) => columns.find((c) => c.title?.toLowerCase().includes(kw.toLowerCase()));

  const googleLocationCol = findCol('google location');
  const maintainedByCol   = findCol('maintained by');
  const siteAreaCol       = findCol('site area');

  if (!googleLocationCol || !maintainedByCol) {
    throw new Error(`Missing columns. Found: ${columns.map((c) => c.title).join(', ')}`);
  }

  const glId = googleLocationCol.id;
  const mbId = maintainedByCol.id;
  const saId = siteAreaCol?.id;
  const lcId = LC_COLUMN_ID;

  const mbMap = buildLabelMap(maintainedByCol.settings_str);

  const colIds = [lcId, glId, mbId, saId].filter(Boolean);

  const itemsRes = await fetch('https://api.monday.com/v2', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': MONDAY_API_KEY,
      'API-Version': '2024-01',
    },
    body: JSON.stringify({
      query: `query {
        boards(ids: ${BOARD_ID}) {
          items_page(limit: 500) {
            items {
              id
              name
              column_values(ids: ${JSON.stringify(colIds)}) {
                id
                text
                value
                ... on BoardRelationValue {
                  linked_items { name }
                }
                ... on StatusValue {
                  label
                  index
                }
                ... on DropdownValue {
                  values { label }
                }
                ... on MirrorValue {
                  display_value
                }
                ... on LocationValue {
                  lat
                  lng
                  address
                }
              }
            }
          }
        }
      }`,
    }),
  });

  const itemsData = await itemsRes.json();
  if (itemsData.errors) throw new Error(itemsData.errors[0]?.message || 'Items error');

  const items = itemsData?.data?.boards?.[0]?.items_page?.items || [];

  function resolveLabel(col, labelMap) {
    if (!col) return '';
    if (Array.isArray(col.linked_items) && col.linked_items.length > 0) {
      const name = col.linked_items[0]?.name;
      if (name) return name.trim();
    }
    if (col.label && typeof col.label === 'string' && col.label.trim()) return col.label.trim();
    if (Array.isArray(col.values) && col.values.length > 0) {
      const labels = col.values.map((v) => v?.label).filter(Boolean);
      if (labels.length) return labels[0];
    }
    if (col.display_value && col.display_value.trim()) return col.display_value.trim();
    if (col.text && col.text.trim()) return col.text.trim();
    if (col.value) {
      try {
        const v = JSON.parse(col.value);
        if (v?.label && typeof v.label === 'string') return v.label;
        if (v?.index != null) {
          const fromMap = labelMap[String(v.index)];
          if (fromMap) return fromMap;
        }
        if (v?.text && typeof v.text === 'string') return v.text;
      } catch (_) {}
    }
    if (col.index != null) return labelMap[String(col.index)] || '';
    return '';
  }

  return items
    .map((item) => {
      const get = (id) => item.column_values.find((c) => c.id === id);

      const location     = resolveLabel(get(lcId), {});
      const maintainedBy = resolveLabel(get(mbId), mbMap);
      const siteArea     = saId ? formatArea(get(saId)?.text || '') : '';

      const glCol = get(glId);
      let lat = null, lng = null;
      if (glCol) {
        if (glCol.lat != null && glCol.lng != null) {
          lat = parseFloat(glCol.lat);
          lng = parseFloat(glCol.lng);
        } else if (glCol.value) {
          try {
            const p = JSON.parse(glCol.value);
            lat = parseFloat(p.lat);
            lng = parseFloat(p.lng);
          } catch (_) {}
        }
      }

      return {
        id: item.id,
        name: stripSiteCode(item.name),
        location,
        siteArea,
        maintainedBy,
        lat,
        lng,
        hasCoords: lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng),
      };
    })
    .filter((p) => p.maintainedBy.toLowerCase().includes('project green'));
}


// ── Count-up (respects reduced motion) ────────────────────────────────────────
function useCountUp(target, duration = 1100) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const safeTarget = Number(target) || 0;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || safeTarget === 0) { setValue(safeTarget); return; }

    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(safeTarget * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
}


function StatTile({ icon, value, label, suffix, loading, index }) {
  const counted = useCountUp(loading ? 0 : value);
  return (
    <motion.div
      className="ourparks-stat"
      data-accent={index % 4}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 340, damping: 30, delay: 0.05 + index * 0.07 }}
    >
      <span className="ourparks-stat__icon" aria-hidden="true">{icon}</span>
      <span className="ourparks-stat__value">
        {loading ? '—' : counted.toLocaleString()}
        {!loading && suffix && <em className="ourparks-stat__suffix">{suffix}</em>}
      </span>
      <span className="ourparks-stat__label">{label}</span>
    </motion.div>
  );
}


// ── Tree pin ──────────────────────────────────────────────────────────────────
function createTreePinIcon(L, active = false) {
  return L.divIcon({
    className: `park-map-pin${active ? ' park-map-pin--active' : ''}`,
    html: `<div class="pin-wrapper">
      <span class="pin-pulse"></span>
      <div class="pin-body">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 22V13"/><path d="M9 8H4l4 5H5l4 5h6l4-5h-3l4-5h-5V3a1 1 0 0 0-2 0v5z"/>
        </svg>
      </div>
    </div>`,
    iconSize: [30, 38], iconAnchor: [15, 38], popupAnchor: [0, -40],
  });
}

const MALTA_CENTER = [35.9375, 14.3754];
const MALTA_ZOOM   = 11;

// hard limits: can't zoom out past MALTA_ZOOM, can't pan off the islands
const MALTA_MIN_ZOOM = MALTA_ZOOM;
const MALTA_BOUNDS   = [[35.70, 14.02], [36.16, 14.76]];


// ── Map ───────────────────────────────────────────────────────────────────────
function ParksMap({ parks, activeParkId, onMapReady, onPopupClose }) {
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef     = useRef({});
  const initializedRef = useRef(false);
  const popupCloseRef  = useRef(onPopupClose);

  useEffect(() => { popupCloseRef.current = onPopupClose; }, [onPopupClose]);

  useEffect(() => {
    if (mapInstanceRef.current || !window.L || !mapRef.current) return;
    const L   = window.L;
    const map = L.map(mapRef.current, {
      center: MALTA_CENTER,
      zoom: MALTA_ZOOM,
      minZoom: MALTA_MIN_ZOOM,
      maxZoom: 19,
      maxBounds: MALTA_BOUNDS,
      maxBoundsViscosity: 1.0,
      zoomControl: false,
      doubleClickZoom: false,
    });
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    L.tileLayer(`https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`, {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/attributions">CARTO</a>',
      minZoom: MALTA_MIN_ZOOM,
      maxZoom: 19,
      updateWhenIdle: false,
      keepBuffer: 4,
      bounds: MALTA_BOUNDS,
    }).addTo(map);

    // closing the popup = leaving the park: reset the view AND clear the selection
    map.on('popupclose', () => {
      if (map._suppressResetOnClose) return;
      map.setView(MALTA_CENTER, MALTA_ZOOM, { animate: true, duration: 0.6 });
      if (popupCloseRef.current) popupCloseRef.current();
    });

    mapInstanceRef.current = map;
    if (onMapReady) onMapReady(map, markersRef);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markersRef.current = {};
      initializedRef.current = false;
    };
  }, [onMapReady]);

  // Tiles only paint for the size Leaflet measured at init — re-measure aggressively.
  useEffect(() => {
    const map = mapInstanceRef.current;
    const el  = mapRef.current;
    if (!map || !el) return;

    const invalidate = () => map.invalidateSize({ animate: false, pan: false });

    const raf    = requestAnimationFrame(invalidate);
    const timers = [80, 300, 800, 1500].map((ms) => setTimeout(invalidate, ms));

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(invalidate);
      ro.observe(el);
    }
    window.addEventListener('resize', invalidate);
    window.addEventListener('orientationchange', invalidate);
    document.addEventListener('visibilitychange', invalidate);

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', invalidate);
      window.removeEventListener('orientationchange', invalidate);
      document.removeEventListener('visibilitychange', invalidate);
    };
  }, []);

  useEffect(() => {
    const L = window.L;
    if (!L || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    Object.values(markersRef.current).forEach((m) => map.removeLayer(m));
    markersRef.current = {};

    parks.filter((p) => p.hasCoords).forEach((park) => {
      const marker = L.marker([park.lat, park.lng], { icon: createTreePinIcon(L, false), riseOnHover: true })
        .addTo(map)
        .bindPopup(
          `<div class="map-popup">
            <strong>${park.name}</strong>
            ${park.location ? `<span>${park.location}</span>` : ''}
            ${park.siteArea ? `<span class="map-popup-area">${park.siteArea}</span>` : ''}
          </div>`,
          { className: 'park-popup' }
        );
      marker.on('click', () => {
        map.flyTo([park.lat, park.lng], 15, { animate: true, duration: 0.8 });
        marker.openPopup();
      });
      markersRef.current[park.id] = marker;
    });

    if (!initializedRef.current) { map.setView(MALTA_CENTER, MALTA_ZOOM); initializedRef.current = true; }
    map.invalidateSize({ animate: false, pan: false });
    if (onMapReady) onMapReady(map, markersRef);
  }, [parks, onMapReady]);

  useEffect(() => {
    const L = window.L;
    if (!L) return;
    Object.entries(markersRef.current).forEach(([id, marker]) => {
      marker.setIcon(createTreePinIcon(L, String(id) === String(activeParkId)));
    });
  }, [activeParkId, parks]);

  return <div ref={mapRef} className="parks-map" aria-label="Parks map" />;
}


// ── Park Card ─────────────────────────────────────────────────────────────────
function ParkCard({ park, index, isActive, onClick }) {
  const area = splitArea(park.siteArea);

  return (
    <motion.article
      layout
      data-accent={index % 4}
      className={`park-card${park.hasCoords ? ' park-card--clickable' : ''}${isActive ? ' park-card--active' : ''}`}
      initial={{ opacity: 0, y: 18, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.16, ease: 'easeIn' } }}
      transition={{
        type: 'spring', stiffness: 430, damping: 33, mass: 0.7,
        delay: Math.min(index * 0.028, 0.3),
      }}
      whileHover={park.hasCoords ? { y: -5 } : undefined}
      whileTap={park.hasCoords ? { scale: 0.985 } : undefined}
      onClick={() => park.hasCoords && onClick(park)}
      role={park.hasCoords ? 'button' : undefined}
      tabIndex={park.hasCoords ? 0 : undefined}
      onKeyDown={(e) => { if (park.hasCoords && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(park); } }}
      aria-label={park.hasCoords ? `View ${park.name} on map` : park.name}
      aria-pressed={park.hasCoords ? isActive : undefined}
    >
      <span className="park-card__rule" aria-hidden="true" />
      <span className="park-card__glow" aria-hidden="true" />

      <div className="park-card__head">
        <div className="park-card__icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22V13"/><path d="M9 8H4l4 5H5l4 5h6l4-5h-3l4-5h-5V3a1 1 0 0 0-2 0v5z"/>
          </svg>
        </div>

        <div className="park-card__body">
          <h2 className="park-card__name" title={park.name}>{park.name}</h2>
          {park.location ? (
            <p className="park-card__location">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
              </svg>
              {park.location}
            </p>
          ) : (
            <p className="park-card__location park-card__location--muted">Locality not set</p>
          )}
        </div>
      </div>

      <div className="park-card__foot">
        {area ? (
          <span className="park-card__area" aria-label={`Site area: ${park.siteArea}`}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6"/>
            </svg>
            <span className="park-card__area-value">{area.value}</span>
            <span className="park-card__area-unit">{area.unit}</span>
          </span>
        ) : (
          <span className="park-card__area park-card__area--empty">Area N/A</span>
        )}

        {park.hasCoords ? (
          <span className="park-card__cta">
            {isActive ? 'On map' : 'View on map'}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </span>
        ) : (
          <span className="park-card__cta park-card__cta--disabled">No coordinates</span>
        )}
      </div>
    </motion.article>
  );
}


function SkeletonCard() {
  return (
    <div className="park-card park-card--skeleton" aria-hidden="true">
      <div className="park-card__head">
        <div className="skeleton skeleton-icon" />
        <div className="park-card__body">
          <div className="skeleton skeleton-name" />
          <div className="skeleton skeleton-loc" />
        </div>
      </div>
      <div className="park-card__foot">
        <div className="skeleton skeleton-chip" />
        <div className="skeleton skeleton-cta" />
      </div>
    </div>
  );
}


// ── Page ──────────────────────────────────────────────────────────────────────
const OurParks = () => {
  const [parks, setParks]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [leafletReady, setLeafletReady] = useState(!!window.L);
  const [activeParkId, setActiveParkId] = useState(null);
  const [locationFilter, setLocationFilter] = useState('');
  const [dropdownOpen, setDropdownOpen]     = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');

  const mapRef         = useRef(null);
  const markersRef     = useRef({});
  const dropdownRef    = useRef(null);
  const mapWrapperRef  = useRef(null);
  const searchInputRef = useRef(null);

  // always land at the top of the page, including on refresh
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    const t = setTimeout(() => window.scrollTo(0, 0), 60);
    return () => clearTimeout(t);
  }, []);

  const handleMapReady = useCallback((mapInstance, markers) => {
    mapRef.current     = mapInstance;
    markersRef.current = markers.current;
  }, []);

  const handlePopupClose = useCallback(() => setActiveParkId(null), []);

  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setLeafletReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    fetchParks()
      .then((data) => { setParks(data); setLoading(false); })
      .catch((err)  => { setError(err.message); setLoading(false); });
  }, []);

  const localities = useMemo(
    () => [...new Set(parks.map((p) => p.location).filter(Boolean))].sort(),
    [parks]
  );

  const displayedParks = useMemo(() => {
    const q = normalizeText(searchQuery.trim());
    return parks.filter((p) => {
      const byLocality = !locationFilter || p.location === locationFilter;
      const byName     = !q || normalizeText(p.name).includes(q);
      return byLocality && byName;
    });
  }, [parks, locationFilter, searchQuery]);

  const totalArea = useMemo(
    () => Math.round(parks.reduce((sum, p) => sum + areaToNumber(p.siteArea), 0)),
    [parks]
  );

  const parksWithCoords = displayedParks.filter((p) => p.hasCoords);

  const resetMapView = useCallback(() => {
    setActiveParkId(null);
    if (mapRef.current) {
      mapRef.current._suppressResetOnClose = true;
      mapRef.current.closePopup();
      mapRef.current._suppressResetOnClose = false;
      mapRef.current.setView(MALTA_CENTER, MALTA_ZOOM, { animate: true, duration: 0.6 });
    }
  }, []);

  const handleLocationSelect = useCallback((loc) => {
    setLocationFilter(loc);
    setDropdownOpen(false);
    resetMapView();
  }, [resetMapView]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setActiveParkId(null);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
    resetMapView();
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [resetMapView]);

  const handleCardClick = useCallback((park) => {
    setActiveParkId(park.id);

    const stacked = window.matchMedia('(max-width: 1099px)').matches;
    if (stacked && mapWrapperRef.current) {
      const top = mapWrapperRef.current.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }

    setTimeout(() => {
      const map    = mapRef.current;
      const marker = markersRef.current[park.id];
      if (map && marker) {
        map._suppressResetOnClose = true;
        map.closePopup();
        map._suppressResetOnClose = false;
        map.invalidateSize({ animate: false, pan: false });
        map.flyTo([park.lat, park.lng], 15, { animate: true, duration: 0.85 });
        setTimeout(() => marker.openPopup(), 500);
      }
    }, stacked ? 320 : 60);
  }, []);

  const feedTitle = locationFilter || (searchQuery.trim() ? 'Search results' : 'All parks');

  return (
    <div className="ourparks-page">
      <NavBar />
<div className="ourparks-banner" style={{ backgroundImage: `url(${OurParksBanner})` }}>
  <div className="ourparks-banner-overlay" />
  <div className="ourparks-banner-text">
    <h2 className="ourparks-banner-heading">Our Parks</h2>
    <p className="ourparks-banner-sub">Discover Our Green Open Spaces</p>
  </div>
</div>

      <div className="ourparks-page-content">

        {/* ── Hero stat panel ── */}
        <div className="ourparks-stats">
          <div className="ourparks-stats__grid">
            <StatTile
              index={0}
              loading={loading}
              value={parks.length}
              label="Open spaces"
              icon={
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22V13"/><path d="M9 8H4l4 5H5l4 5h6l4-5h-3l4-5h-5V3a1 1 0 0 0-2 0v5z"/>
                </svg>
              }
            />
            <StatTile
              index={1}
              loading={loading}
              value={localities.length}
              label="Localities"
              icon={
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
                </svg>
              }
            />
            <StatTile
              index={2}
              loading={loading}
              value={totalArea}
              suffix="m²"
              label="Total green area"
              icon={
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6"/>
                </svg>
              }
            />
          </div>
        </div>

        <div className="ourparks-layout">

          {/* ── Map column (left on desktop, full height) ── */}
          <div className="ourparks-map-col" ref={mapWrapperRef}>
            <div className="ourparks-map-wrapper">
              <div className="ourparks-map-bar">
                <span className="ourparks-map-bar__title">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 20 3 17V4l6 3 6-3 6 3v13l-6-3-6 3Z"/><path d="M9 7v13M15 4v13"/>
                  </svg>
                  Interactive map
                </span>
                <span className="map-stat-pill">
                  {parksWithCoords.length} plotted
                </span>
              </div>

              {leafletReady ? (
                <ParksMap
                  parks={displayedParks}
                  activeParkId={activeParkId}
                  onMapReady={handleMapReady}
                  onPopupClose={handlePopupClose}
                />
              ) : (
                <div className="parks-map parks-map--loading">
                  <div className="map-loading-inner">
                    <div className="map-loading-spinner" />
                    <span>Loading map…</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Feed column (right on desktop) ── */}
          <div className="ourparks-feed">
            <div className="ourparks-feed-header">
              <div className="ourparks-feed-heading">
                <h2 className="ourparks-feed-title">{feedTitle}</h2>
                {!loading && (
                  <span className="ourparks-feed-count">
                    {displayedParks.length} site{displayedParks.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {!loading && !error && (
                <div className="ourparks-feed-tools">
                  {/* Search by park name */}
                  <div className={`parks-search${searchQuery ? ' parks-search--filled' : ''}`}>
                    <svg className="parks-search__icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="11" cy="11" r="7"/><path d="m20 20-3.6-3.6"/>
                    </svg>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="parks-search__input"
                      placeholder="Search parks…"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onKeyDown={(e) => { if (e.key === 'Escape') handleSearchClear(); }}
                      aria-label="Search parks by name"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="parks-search__clear"
                        onClick={handleSearchClear}
                        aria-label="Clear search"
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M18 6 6 18M6 6l12 12"/>
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Locality filter dropdown */}
                  <div className="parks-location-dropdown" ref={dropdownRef}>
                    <button
                      className={`parks-filter-btn${locationFilter ? ' parks-filter-btn--active' : ''}`}
                      onClick={() => setDropdownOpen((o) => !o)}
                      aria-haspopup="listbox"
                      aria-expanded={dropdownOpen}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span className="parks-filter-btn__label">{locationFilter || 'Filter by Locality'}</span>
                      {locationFilter ? (
                        <span className="parks-filter-btn__clear" role="button" aria-label="Clear filter"
                          onClick={(e) => { e.stopPropagation(); handleLocationSelect(''); }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M18 6 6 18M6 6l12 12"/>
                          </svg>
                        </span>
                      ) : (
                        <svg className={`parks-filter-btn__chevron${dropdownOpen ? ' parks-filter-btn__chevron--open' : ''}`}
                          width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M6 9l6 6 6-6"/>
                        </svg>
                      )}
                    </button>

                    <AnimatePresence>
                      {dropdownOpen && (
                        <motion.ul
                          className="parks-dropdown-list"
                          role="listbox"
                          aria-label="Filter by locality"
                          initial={{ opacity: 0, y: -8, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.96, transition: { duration: 0.13 } }}
                          transition={{ type: 'spring', stiffness: 480, damping: 32, mass: 0.6 }}
                        >
                          <li
                            className={`parks-dropdown-item${!locationFilter ? ' parks-dropdown-item--selected' : ''}`}
                            role="option" aria-selected={!locationFilter}
                            onClick={() => handleLocationSelect('')}
                          >
                            All Localities
                            <span className="parks-dropdown-item__count">{parks.length}</span>
                          </li>
                          {localities.length === 0 ? (
                            <li className="parks-dropdown-item parks-dropdown-item--empty">No localities available</li>
                          ) : localities.map((loc) => (
                            <li
                              key={loc}
                              className={`parks-dropdown-item${locationFilter === loc ? ' parks-dropdown-item--selected' : ''}`}
                              role="option" aria-selected={locationFilter === loc}
                              onClick={() => handleLocationSelect(loc)}
                            >
                              {loc}
                              <span className="parks-dropdown-item__count">{parks.filter((p) => p.location === loc).length}</span>
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="ourparks-error" role="alert">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span><strong>Could not load parks.</strong> {error}</span>
              </div>
            )}

            {loading ? (
              <div className="parks-grid">
                {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : displayedParks.length === 0 && !error ? (
              <div className="ourparks-empty">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M12 22V13"/><path d="M9 8H4l4 5H5l4 5h6l4-5h-3l4-5h-5V3a1 1 0 0 0-2 0v5z"/>
                </svg>
                <h3>
                  {searchQuery.trim()
                    ? `No parks match “${searchQuery.trim()}”`
                    : locationFilter
                      ? `No parks in ${locationFilter}`
                      : 'No parks found'}
                </h3>
                <p>
                  {searchQuery.trim()
                    ? 'Try a different name or clear the search.'
                    : locationFilter
                      ? 'Try a different locality.'
                      : 'No Project Green parks found on the board.'}
                </p>
                {searchQuery.trim() && (
                  <button className="ourparks-empty__btn" onClick={handleSearchClear}>Clear search</button>
                )}
              </div>
            ) : (
              <div className="parks-grid" id="parks-grid">
                <AnimatePresence mode="popLayout" initial={false}>
                  {displayedParks.map((park, index) => (
                    <ParkCard
                      key={park.id}
                      park={park}
                      index={index}
                      isActive={activeParkId === park.id}
                      onClick={handleCardClick}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OurParks;